import {createSlice, PayloadAction} from "@reduxjs/toolkit"
import dayjs from "dayjs"
import {AppThunk} from "../../rootReducer"
import {getAccessToken, getSelectedRegionId} from "../../utils/common"
import httpService from "../../utils/httpService"
import {SelectionRange} from "@progress/kendo-react-dateinputs"

export type TestBooking = {
    bookingID: number
    bookingRef: string
    vehicleID: number | null
    dealerID: number
    customerID: number | null
    accountNo: string | null
    companyName: string | null
    testStationID: number | null
    jobNumber: string | null
    customerOrderRef: string | null
    testPackageID: number | null
    testStart: Date
    testEnd: Date
    testFee: number | null
    laneFee: number | null
    bookingStatusID: number | null
    bookingTypeID: number
    addedByAppUserID: string | null
    addedDate: Date | null
    updatedByAppUserID: string | null
    updatedDate: Date | null
    bookingType: string | null
    bookingStatus: string | null
    tranType: string | null
    reminderCall: boolean | null
    testStationName: string | null
    testPackageName: string | null
    registration: string | null
    internal: boolean | null
    vehicleType: string | null
    testType: string | null
    preparerID: string | null
    testResult: string | null
    motNo: string | null
    expiryDate: Date | null
    failureReason: string | null
    failureItemNo: string | null
}

export interface MonthlyReport {
    dealerId: number | null
    monthID: string | null
    monthYear: string | null
    motAccount: string | null
    dealerNumber: string | null
    regionName: string | null
    dealerName: string | null
    fail: number | null
    pass: number | null
    tests: number | null
    fttp: number | null
    expanded: boolean
    mrId: number | null
    testBookings: TestBooking[]
    bookingTypeID: number | null
    reminderCall: boolean | null
    testStart: any
    testEnd: any
}
type DateRangeComputeResult = {
    validPass: boolean
    notValidPassReason: string
    motDateId?: string|null
    motDate: string
    bookDate: string
    expiryDate:string
    motMonthYear:string
    annualTests:any
}
export interface DateRangeReport {
    bookingID :number
    preparerID: string
    bookingRef: string
    registration: string
    vehicleType: string
    customerName: string
    testDate: string
    testType: string
    testResult: string
    motNo: string
    expiryDate: string
    failureItemNo: string
    failureReason: string
    monthYear: string
    monthID: string
    regionName: string
    preparerDealerName: string
    dealerName: string
    validPass: string
    notValidPassReason: string
    bookingTestDate: Date| null
    motTestDate: Date| null
    motExpiryDate: Date| null
}


export interface DealerReport {
    motAccount: string | null
    regionName: string | null
    preparerDealerName: string | null
    fail: number | null
    pass: number | null
    tests: number | null
    passRate: number | null
    leaguePosition: number | null
}

export interface RegionReport {
    regionName: string | null
    fail: number | null
    pass: number | null
    tests: number | null
    ftpp: number | null
    perOfPass: number | null
    perOfFail: number | null
}

export interface StatisticsReport {
    fail: number 
    pass: number 
    tests: number 
    ftp: string
}

type PassRateReportState = {
    prRegionReport: RegionReport[]
    prRegionReportLoader: boolean
    prDealersReport: DealerReport[]
    prDealersReportLoader: boolean
    prMonthlyReport: MonthlyReport[]
    prMonthlyReportLoader: boolean
    prDateRangeReport: DateRangeReport[]
    prDateRangeReportLoader: boolean
    setYear: number
    summaryDateRangeFilter: {start: Date | null; end: Date | null}
}

const initialState: PassRateReportState = {
    prRegionReport: [],
    prRegionReportLoader: false,
    prDealersReport: [],
    prDealersReportLoader: false,
    prMonthlyReport: [],
    prMonthlyReportLoader: false,
    prDateRangeReport: [],
    prDateRangeReportLoader: false,
    setYear: 0,
    summaryDateRangeFilter: {start: dayjs().startOf("month").toDate(), end: new Date(dayjs().endOf("month").toDate())},
}

const passRateReportSlice = createSlice({
    name: "passratereport",
    initialState,
    reducers: {
        setRegionReport(state, action: PayloadAction<RegionReport[]>) {
            state.prRegionReport = action.payload
        },
        setRegionReportLoader(state, action: PayloadAction<boolean>) {
            state.prRegionReportLoader = action.payload
        },
        setDealerReport(state, action: PayloadAction<DealerReport[]>) {
            state.prDealersReport = action.payload
        },
        setDealerReportLoader(state, action: PayloadAction<boolean>) {
            state.prDealersReportLoader = action.payload
        },
         setDateRangeReport(state, action: PayloadAction<DateRangeReport[]>) {
            state.prDateRangeReport = action.payload
        },
        setDateRangeReportLoader(state, action: PayloadAction<boolean>) {
            state.prDateRangeReportLoader = action.payload
        },
        setYear(state, action: PayloadAction<number>) {
            state.setYear = action.payload
        },
        setPRDateRangeFilter(state, action: PayloadAction<SelectionRange>) {
            state.summaryDateRangeFilter = action.payload
        },
    },
})
export const {
    setRegionReport,
    setRegionReportLoader,
    setDealerReport,
    setDealerReportLoader,
    setDateRangeReport,
    setDateRangeReportLoader,
    setYear,
    setPRDateRangeFilter,
} = passRateReportSlice.actions

export default passRateReportSlice.reducer

const dateFormat = "YYYY-MM-DDTHH:mm:ss"

//Fetch Region Report
export const fetchRegionReport =
    (param: SelectionRange): AppThunk =>
    (dispatch: any, getState) => {
        const regionId = getSelectedRegionId(getState())
        dispatch(setRegionReportLoader(true))
        const startDate = dayjs(param.start).startOf("day").format(dateFormat)
        const endDate = dayjs(param.end).startOf("day").format(dateFormat)

        httpService(getAccessToken(getState()))
            .get(`regions/${regionId}/passrate-region?startDate=${startDate}&endDate=${endDate}`)
            .then(res => {
                dispatch(setRegionReport(res.data))
                dispatch(setRegionReportLoader(false))
            })
            .catch(err => {
                dispatch(setRegionReport([]))
                dispatch(setRegionReportLoader(false))
            })
    }

//fetch dealer report

export const fetchDealerReport =
    (param: SelectionRange): AppThunk =>
    (dispatch: any, getState) => {
        const regionId = getSelectedRegionId(getState())
        dispatch(setDealerReportLoader(true))
        const startDate = dayjs(param.start).startOf("day").format(dateFormat)
        const endDate = dayjs(param.end).startOf("day").format(dateFormat)

        httpService(getAccessToken(getState()))
            .get(`regions/${regionId}/passrate-dealer?startDate=${startDate}&endDate=${endDate}`)
            .then(res => {
                dispatch(setDealerReport(res.data))
                dispatch(setDealerReportLoader(false))
            })
            .catch(err => {
                dispatch(setDealerReport([]))
                dispatch(setDealerReportLoader(false))
            })
    }

//fetch const fetch daterange filter for summary report
export const fetchDateRangeReport =
    (dateRange: SelectionRange, preparerIds: string|null): AppThunk =>
    (dispatch: any, getState) => {
        const regionId = getSelectedRegionId(getState())
        dispatch(setDateRangeReportLoader(true))
        if(!preparerIds) {
            dispatch(setDateRangeReport([]))
            dispatch(setDateRangeReportLoader(false))
            return
        }
        const startDate = dayjs(dateRange.start).startOf("day").format(dateFormat)
        const endDate = dayjs(dateRange.end).startOf("day").format(dateFormat)
        httpService(getAccessToken(getState()))
            .get(`regions/${regionId}/passrate-daterange?startDate=${startDate}&endDate=${endDate}&preparerIds=${preparerIds}`)
            .then(res => {
                    const formatAsTime = (dateColumn: Date | null) =>
                        dateColumn !== null ? dayjs(dateColumn).format("HH:mm") : null
                    const formatAsDate = (dateColumn: Date | null) =>
                        dateColumn !== null ? dayjs(dateColumn).format("DD/MM/YYYY") : null
                    const {annualTests:annualTestsMaps,bookings} = res.data
                    dispatch(
                        setDateRangeReport(
                            bookings.map((data: any) => {
                                const { validPass, notValidPassReason, motDateId, bookDate, expiryDate, motMonthYear,annualTests} = computeValidPassForDateRangeReport(data,annualTestsMaps,formatAsDate, formatAsTime)
                                return{
                                    bookingID :data.bookingID,
                                    preparerID:data.preparerID,
                                    preparerDealerName: data.preparerDealerName,
                                    bookingRef: data.bookingRef,
                                    registration: data.vehicle?.registration,
                                    vehicleType: data.vehicle?.vehicleType,
                                    customerName: data.companyName,
                                    testDate: bookDate,
                                    testType:setDateRangeReportIfValid(validPass, annualTests?.testType),
                                    testResult: setDateRangeReportIfValid(validPass, annualTests?.testResult),
                                    motNo: setDateRangeReportIfValid(validPass, annualTests?.testCertificateNumber),
                                    expiryDate: setDateRangeReportIfValid(validPass, expiryDate),
                                    failureItemNo:setDateRangeReportIfValid(validPass, annualTests?.defects?.[0]?.failureItemNo),
                                    failureReason:setDateRangeReportIfValid(validPass, setFailureReason(annualTests)),
                                    monthYear:setDateRangeReportIfValid(validPass, motMonthYear),
                                    monthID:setDateRangeReportIfValid(validPass, motDateId),
                                    regionName: data.dealer?.region?.regionName,
                                    dealerName: data.dealer?.dealerName,
                                    validPass: validPass? "Yes":"No",
                                    notValidPassReason,
                                    bookingTestDate:new Date(data.testStart),
                                    motTestDate:setDateRangeReportIfValid(validPass, setDateRangeReportIfValid(annualTests?.testDate, new Date(annualTests?.testDate))),
                                    motExpiryDate: setDateRangeReportIfValid(validPass, setDateRangeReportIfValid(annualTests?.expiryDate, new Date(annualTests?.expiryDate)))
                                }
                            },
                        ),
                    ),
                )
                dispatch(setDateRangeReportLoader(false))
            })
            .catch(err => {
                dispatch(setDateRangeReport([]))
                dispatch(setDateRangeReportLoader(false))
            })
    }
    const setDateRangeReportIfValid=(validPass:boolean,obj:any)=>validPass?obj:null

    const setFailureReason=(annualTests:any):string=>{
        let reason = ""
        if(annualTests?.testResult === "FAILED"&&annualTests?.defects && annualTests?.defects.length > 0){
            reason = annualTests.defects.map((defect:any)=>defect.failureReason).join(";")
        }
        return reason
    }

    const computeValidPassForDateRangeReport = (data: any, annualTestMaps: any, formatAsDate: any, formatAsTime: any): DateRangeComputeResult => {
            let dateRangeComputeResult:DateRangeComputeResult={
                validPass :true,
                notValidPassReason :"",
                motDateId: "",
                motDate: "",
                bookDate:formatAsDate(data.testStart),
                expiryDate: "",
                motMonthYear: "",
                annualTests: undefined
            }
            const annualTests =setDateRangeReportIfValid(data.vehicleID ,annualTestMaps[data.vehicleID])
           
            if(validMissVehicle(data)){
                return {
                    ...dateRangeComputeResult,
                    validPass : false,
                    notValidPassReason : "Missing/Incorrect Vehicle details",
                }
            }
            if(validDuplicate(annualTests,data.testStart)){
                return{
                    ...dateRangeComputeResult,
                    validPass : false,
                    notValidPassReason : "Duplicate - Recent MOT detected",
                }
            }
            const {isDateMismatch,annualTest}=validDateMismatch(annualTests, data.testStart,formatAsDate)
            if(isDateMismatch){
                return{
                    ...dateRangeComputeResult,
                    validPass : false,
                    notValidPassReason : "Booking and MOT dates mismatch",
                }
            }
          
            const testDate=annualTest.testDate 
            const expiryDate=annualTest.expiryDate 
                dateRangeComputeResult={
                ...dateRangeComputeResult,
                motDateId:formatAsTime(testDate),
                motDate:formatAsDate(testDate),
                expiryDate: formatAsDate(expiryDate),
                motMonthYear: testDate ? dayjs(testDate).format("MMM-YYYY") : "",
                annualTests: annualTest
            }
            
            return dateRangeComputeResult
    }
   
    const  validMissVehicle=(data:any)=>{
        return !data.vehicle || !data.vehicle.registration
    }
    const validDuplicate = (annualTests: any[], startDate: string) => {
        const maxDate = dayjs(startDate).startOf("day")
        const minDate = maxDate.subtract(3, "month")
        return (
            annualTests &&
            annualTests.length > 1 &&
            annualTests.some((test: any) => {
            const testDate = dayjs(test.testDate)
            return testDate.isBefore(maxDate) && testDate.isAfter(minDate)
            })
        )
    }
    const  validDateMismatch=(annualTests:any[]= [],bookingTestStart:any,formatAsDate:any)=>{
        const bookingTestDate=formatAsDate(bookingTestStart)
        const annualTest=annualTests.find((test:any)=>formatAsDate(test.testDate) === bookingTestDate)
        return { 
            isDateMismatch:!annualTest,
            annualTest
        }
    }